import { AccAnchor } from "@components/base/AccAnchor";
import React from "react";
import BlockquoteTick from "@images/BlockquoteTicks.svg";
import { GridListRight, GridListLeft, GridList } from "@ui/ProductPages";
import MDXLink from "@components/MDXLink";
import Markdown from "markdown-to-jsx";
import { LinkIcon } from "@heroicons/react/outline";
import { MarkdownToJSX } from "markdown-to-jsx";
import GradientHeroTitle from "@ui/GradientHeroTitle";
import OrangeText from "@ui/OrangeText";
import NumberFormat from "react-number-format";
import YoutubeMovie from "@ui/YoutubeVideo";
import removeAngleBrackets from "./removeAngleBrackets";
import VimeoMovie from "@ui/VimeoVideo";
import cn from "classnames";

/** Custom Rendering for specific Markdown Elements */
export const CustomMarkdownComponents = {
  h1: (props: any) => <h1 className="text-4xl font-bold" {...props} />,
  // Replicate: https://www.gatsbyjs.com/plugins/gatsby-remark-autolink-headers/
  // as MDX does not work with Strapi v4
  h2: ({ ...props }) => {
    return (
      <h2 {...props} className={cn("group relative mb-9", props.className)}>
        {props.children}
        {props.id && (
          <AccAnchor
            href={`#${props.id}`}
            className="invisible group-hover:visible"
          >
            <LinkIcon className="ml-2 inline-block h-4 w-4" />
          </AccAnchor>
        )}
      </h2>
    );
  },
  h3: ({ ...props }) => {
    return (
      <h3 {...props} className={cn("group relative mb-2", props.className)}>
        {props.children}
        {props.id && (
          <AccAnchor
            href={`#${props.id}`}
            className="invisible group-hover:visible"
          >
            <LinkIcon className="ml-2 inline-block h-3 w-3" />
          </AccAnchor>
        )}
      </h3>
    );
  },
  blockquote: ({ ...props }) => {
    const { children } = props;

    return (
      <div className="flex flex-row items-start">
        <div>
          <img
            className="mt-1"
            style={{
              minWidth: "12px",
            }}
            src={BlockquoteTick}
            alt="tick"
          />
        </div>
        <blockquote className="text-xl">{children}</blockquote>
      </div>
    );
  },
  a: (props: any) => <MDXLink {...props} />,
  p: (props: any) => {
    // if image is inside paragraph then return the image. It prevents hydration issue of div rendered inside p tag.
    // Issue was caused by Gatsby Image component added instead img inside p tag.
    if (
      props.children?.some((child: any) => ["img"].includes(child?.type?.name))
    ) {
      return props.children;
    }
    return <p className={cn(props.className)}>{props.children}</p>;
  },
};

// lowercase version of tags because of possible translation conversion of tags from PascalCase to lowercase ie. for Korean
export const Markdown2JSX: MarkdownToJSX.Overrides = {
  ...CustomMarkdownComponents,
  GradientHeroTitle: GradientHeroTitle,
  OrangeText: OrangeText,
  gradientherotitle: GradientHeroTitle,
  orangetext: OrangeText,
  FormatNumber: ({ ...props }) => {
    const { children } = props;
    return (
      <NumberFormat
        value={children[0]}
        displayType={"text"}
        thousandSeparator={true}
      />
    );
  },
  a: MDXLink,
  GridList: GridList,
  GridListLeft: GridListLeft,
  GridListRight: GridListRight,
  Youtube: YoutubeMovie,
  youtube: YoutubeMovie,
  Vimeo: VimeoMovie,
  vimeo: VimeoMovie,
  img: ({ ...props }: any) => {
    props.src = removeAngleBrackets(props.src);
    // if not string then don't render
    if (typeof props.src !== "string") return null;
    // check if it starts with one of valid values
    if (
      props.src?.startsWith("/") ||
      props.src?.startsWith("http") ||
      props.src?.startsWith("data:")
    ) {
      return <img {...props} />;
    }
    return null;
  },
};

export const MarkdownPageContext = ({
  children,
  overrides = {},
  options = {},
}: {
  children: React.ReactNode;
  overrides?: MarkdownToJSX.Overrides;
  options?: MarkdownToJSX.Options;
}) => {
  let markdown = children?.toString() || "";
  // replace tags that are not allowed in body.
  markdown = markdown.replace(
    /<(\/?(html|head|body|meta|link)[^>]*)>/gim,
    "<code>&lt;$1&gt;</code>"
  );
  return (
    <Markdown
      options={{
        overrides: {
          ...Markdown2JSX,
          ...overrides,
        },
        enforceAtxHeadings: true,
        wrapper: React.Fragment,
        forceWrapper: true,
        ...options,
      }}
    >
      {markdown}
    </Markdown>
  );
};

type MDXProps = {
  data: {
    childMarkdownRemark: Queries.MarkdownNodeFragment;
  };
};

export const mdxProps = (props: MDXProps) => {
  const { childMarkdownRemark } = props.data;
  return { ...childMarkdownRemark };
};
